import { flattenDepth } from 'lodash';

const getDateWithoutTimePart = date => new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate(),
  0, 0, 0, 0,
);

const getTodayDate = () => getDateWithoutTimePart(new Date());

const nextDate = date => new Date(date.setDate(date.getDate() + 1));

/* eslint-disable max-len */
const datesDatePartIsEqual = (date1, date2) => getDateWithoutTimePart(date1).getTime() === getDateWithoutTimePart(date2).getTime();

/**
 * Получение даты со временем из строк типа "10:30"
 * @param time {string}
 * @returns {Date}
 */
const getTimeAsMinutes = (time) => {
  const [timeHours, timeMinutes] = time.split(':');
  const todayDate = getTodayDate();
  todayDate.setHours(timeHours);
  todayDate.setMinutes(timeMinutes);
  return todayDate;
};

/**
 * Функция определяет, работает ли пункт обслуживания в данный момент с учетом обеда.
 * @param from {string} - Время начала рабочего дня.
 * @param to {string} - Время окончания рабочего дня.
 * @param lunch {object} - Информация об обеде.
 * @param lunch.from {string} - Время начала обеда.
 * @param lunch.to {string} - Время окончания обеда.
 * @returns {{time: *, open: *, lunchStatus: *}}
 */
const isWorkTime = (from, to, lunch) => {
  const currentTime = new Date();
  const dayTimeFrom = getTimeAsMinutes(from);
  const dayTimeTo = getTimeAsMinutes(to);
  if (dayTimeFrom > dayTimeTo) {
    nextDate(dayTimeTo);
  }

  let openStatus = false;
  let openTime = from;
  if (dayTimeFrom < currentTime && currentTime < dayTimeTo) {
    openStatus = true;
    openTime = to;
  }

  let dayLunchFrom = null;
  let dayLunchTo = null;
  let lunchStatus = false;
  const isLunch = (lunch && lunch.from && lunch.to);
  if (isLunch) {
    dayLunchFrom = getTimeAsMinutes(lunch.from);
    dayLunchTo = getTimeAsMinutes(lunch.to);

    if (dayLunchFrom < currentTime && currentTime < dayLunchTo) {
      lunchStatus = true;
    }
  }

  return {
    open: openStatus,
    time: openTime,
    lunchStatus,
  };
};

/**
 * Функция определяет, входит ли конкретный день в диапазон дней.
 * @param srtCheckDays {string} - Диапазон дней.
 * @param currentDay {string} - Искомый день.
 * @param mapWeek {array} - Список дней недель по порядку.
 * Например для `ru` зоны, неделя начинается с понедельника, массив будет выглядеть
 * ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'], а для `en` зоны -
 * ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], начинается с воскресенья.
 * @returns {boolean}
 * @example
 *
 * isIncludesDay('Пн, Ср-Пт', 'Вт', ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']) // false
 * isIncludesDay('Пн, Ср-Пт', 'Ср', ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']) // true
 * isIncludesDay('Sun-Tue, Thu', 'Wed', ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']) // false
 * isIncludesDay('Sun-Tue, Thu', 'Mon', ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']) // true
 */
const isIncludesDay = (srtCheckDays, currentDay, mapWeek) => {
  const arrWorkDay = srtCheckDays
    .split(',')
    .map((day) => {
      const daySplit = day.trim().split('-');
      const resultDay = [];

      if (daySplit.length !== 1) {
        const [startDay, finishDay] = daySplit;
        const startDayId = mapWeek.indexOf(startDay);
        const finishDayId = mapWeek.indexOf(finishDay);

        for (let dayIndex = startDayId; dayIndex <= finishDayId; dayIndex += 1) {
          resultDay.push(mapWeek[dayIndex]);
        }
      } else {
        resultDay.push(daySplit[0]);
      }
      return resultDay;
    });

  const arrWorkDayFlat = flattenDepth(arrWorkDay, 1);

  return arrWorkDayFlat.includes(currentDay);
};
const isWorkDay = isIncludesDay;


/**
 * Функция определяет, работает ли пункт обслуживания
 * @param days {string} - Диапазон дней
 * @param from {string} - Время начала рабочего дня.
 * @param to {string} - Время окончания рабочего дня.
 * @param lunch {object} - Информация об обеде.
 * @param lunch.days {string||null} - Дни проведения обеда,
 * если null, то обед есть во всех рабочих днях.
 * @param lunch.from {string} - Время начала обеда.
 * @param lunch.to {string} - Время окончания обеда.
 * @param currentDay {string} - Искомый день.
 * @param mapWeek {array} - Список дней недель по порядку.
 * @returns {object|null}
 */
const isWorkNow = ({
  days, from, to, lunch, currentDay, mapWeek,
}) => {
  let result = {
    open: false,
    time: null,
    lunchStatus: false,
  };
  const stringDaysSearched = days;
  const resultIncludesDay = isIncludesDay(stringDaysSearched, currentDay, mapWeek);

  if (resultIncludesDay) {
    if (from && to) {
      let resultLunchDay = true;
      if (lunch.days) {
        // eslint-disable-next-line no-shadow
        const stringDaysSearched = lunch.days;
        resultLunchDay = isIncludesDay(stringDaysSearched, currentDay, mapWeek);
      }
      result = isWorkTime(from, to, resultLunchDay ? lunch : {});
    }
  }

  return result;
};

export {
  nextDate,
  datesDatePartIsEqual,
  getDateWithoutTimePart,
  getTimeAsMinutes,
  getTodayDate,
  isWorkNow,
  isIncludesDay,
  isWorkDay,
};

export default {
  nextDate,
  datesDatePartIsEqual,
  getDateWithoutTimePart,
  getTimeAsMinutes,
  getTodayDate,
  isWorkNow,
  isIncludesDay,
  isWorkDay,
};
